import { TFunction } from "i18next";
import { Waybill } from "Models/waybill";
import { CompanyProfile, ShipmentType } from "Util/enums";
import * as yup from "yup";

import { buildAirPublishSchema, buildSeaPublishSchema } from "./waybillSchemas";

export type ValidateWaybillForDraftResult =
  | { ok: true }
  | { ok: false; errors: yup.ValidationError[] };

export type ValidateWaybillForDraftOptions = {
  companyProfileInitials?: string;
};

/**
 * Same rules as ShipmentMaintenance "Save as draft" (onSave): air uses onValidateAirSchema
 * (isSubscribed omitted so when("isSubscribed", { is: true }) branches are skipped);
 * sea uses onValidate(shipment, !!marineTrafficId).
 */
export function validateWaybillForDraft(
  waybill: Waybill,
  translate: TFunction,
  options: ValidateWaybillForDraftOptions = {}
): ValidateWaybillForDraftResult {
  const isMvCompany = options.companyProfileInitials === CompanyProfile.MV;
  const isAir = waybill?.shipmentType?.toLowerCase() === ShipmentType.AIR.toLowerCase();

  if (isAir) {
    const airPayload = {
      commodity: waybill?.commodity,
      containerId: waybill?.containerId,
      shipmentType: waybill?.shipmentType,
      jobs: waybill?.jobs,
      customers: waybill?.customers,
    };

    try {
      buildAirPublishSchema(translate, isMvCompany).validateSync(airPayload, { abortEarly: false });
    } catch (error: unknown) {
      if (error instanceof yup.ValidationError) {
        const errors = error.inner?.length ? error.inner : [error];
        return { ok: false, errors };
      }
      return { ok: false, errors: [] };
    }

    return { ok: true };
  }

  const seaPayload = {
    isSubscribed: !!waybill?.marineTrafficId,
    containerId: waybill?.containerId,
    shipmentType: waybill?.shipmentType,
    loadType: waybill?.loadType,
    billOfLanding: waybill?.billOfLanding,
    jobs: waybill?.jobs,
    containers: waybill?.containers,
    customers: waybill?.customers || waybill?.customersToShipment,
  };

  try {
    buildSeaPublishSchema(translate).validateSync(seaPayload, { abortEarly: false });
  } catch (error: unknown) {
    if (error instanceof yup.ValidationError) {
      const errors = error.inner?.length ? error.inner : [error];
      return { ok: false, errors };
    }
    return { ok: false, errors: [] };
  }

  return { ok: true };
}
